import { useEffect } from 'react';

interface SEOMeta {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
}

export default function useSEO(meta: SEOMeta) {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

    // Title
    document.title = meta.title;

    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        const parts = selector.match(/\[([^=]+)="([^"]+)"\]/);
        if (parts) el.setAttribute(parts[1], parts[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', meta.description);
    if (meta.keywords) setMeta('meta[name="keywords"]', 'content', meta.keywords);

    // Canonical
    const canonicalPath = meta.canonical ?? '';
    let canonicalEl = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.rel = 'canonical';
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = `${siteUrl}${canonicalPath}`;

    // Robots
    if (meta.noIndex) {
      setMeta('meta[name="robots"]', 'content', 'noindex, nofollow');
    } else {
      setMeta('meta[name="robots"]', 'content', 'index, follow');
    }

    // OG tags
    const ogTitle = meta.ogTitle ?? meta.title;
    const ogDesc = meta.ogDescription ?? meta.description;
    const ogImage = meta.ogImage ?? `${siteUrl}/og-image.jpg`;
    const ogType = meta.ogType ?? 'website';

    setMeta('meta[property="og:title"]', 'content', ogTitle);
    setMeta('meta[property="og:description"]', 'content', ogDesc);
    setMeta('meta[property="og:image"]', 'content', ogImage);
    setMeta('meta[property="og:type"]', 'content', ogType);
    setMeta('meta[property="og:url"]', 'content', `${siteUrl}${canonicalPath}`);

    // Twitter
    setMeta('meta[name="twitter:title"]', 'content', ogTitle);
    setMeta('meta[name="twitter:description"]', 'content', ogDesc);
    setMeta('meta[name="twitter:image"]', 'content', ogImage);
  }, [meta.title, meta.description, meta.keywords, meta.canonical, meta.ogTitle, meta.ogDescription, meta.ogImage, meta.ogType, meta.noIndex]);
}
